import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import OnboardingBanner from '../../components/onboarding/OnboardingBanner';
import { useAuth } from '../../contexts/AuthContext';
import { isPlanFeatureLocked } from '../../utils/planFeatures';
import AssetHealthPanel from '../../components/assets/AssetHealthPanel';
import {
  analyticsAPI, monitoringAPI, seoAPI, subscriptionAPI,
  serverAPI, aiSecurityAPI, visitorLogAPI, alertsAPI,
} from '../../services/api';

// ── Types ─────────────────────────────────────────────────────────────────────

interface VisitorRow {
  visitor_id: string;
  country?: string;
  city?: string;
  first_page?: string;
  started_at?: string;
  device_type?: string;
  _isNew?: boolean;
}

interface HealthPoint {
  time: string;
  status: 'up' | 'down';
  response_time: number;
  url: string;
}

interface HealthCheck {
  url: string;
  status: 'up' | 'down';
  response_time?: number;
  last_checked?: string;
}

interface ServerRow {
  id: number;
  name: string;
  status: string;
  last_seen?: string;
  current_metrics?: {
    cpu_usage_percent?: number | null;
    memory_usage_percent?: number | null;
    disk_worst_percent?: number | null;
  } | null;
}

interface KwRank {
  keyword: string;
  google: { rank: number; checked_at: string } | null;
  bing: { rank: number } | null;
}

interface AppItem {
  title: string;
  icon: string;
  route: string;
  feature?: string;
  description: string;
  gradient: string;
}

// ── Apps ──────────────────────────────────────────────────────────────────────

const APP_ITEMS: AppItem[] = [
  { title: 'Website', icon: 'bi-globe2', route: '/analytics/summary', feature: 'analytics', description: 'Visitors, uptime & speed', gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)' },
  { title: 'Security', icon: 'bi-shield-fill-check', route: '/security/ai-advisor', feature: 'security', description: 'Scans, vulnerabilities & email security', gradient: 'linear-gradient(135deg,#f43f5e,#e11d48)' },
  { title: 'Servers & Devices', icon: 'bi-hdd-stack-fill', route: '/servers', feature: 'server_monitoring', description: 'Servers, laptops & desktops', gradient: 'linear-gradient(135deg,#f59e0b,#d97706)' },
  { title: 'SEO', icon: 'bi-graph-up-arrow', route: '/seo/summary', feature: 'seo', description: 'Boost search rankings', gradient: 'linear-gradient(135deg,#10b981,#059669)' },
  { title: 'Feedback', icon: 'bi-chat-square-text-fill', route: '/feedback', feature: 'feedback', description: 'Collect customer feedback', gradient: 'linear-gradient(135deg,#ec4899,#db2777)' },
  { title: 'App Analytics', icon: 'bi-phone-fill', route: '/app-analytics/overview', feature: 'analytics', description: 'Mobile app users & funnels', gradient: 'linear-gradient(135deg,#7c3aed,#6366f1)' },
  { title: 'Settings', icon: 'bi-gear-fill', route: '/assets', description: 'Assets, team, notifications & billing', gradient: 'linear-gradient(135deg,#94a3b8,#64748b)' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

const timeAgo = (iso?: string): string => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h`;
};

const shortPage = (page?: string): string => {
  if (!page) return '/';
  try { return new URL(page).pathname.slice(0, 24) || '/'; } catch { return page.slice(0, 24); }
};

const shortHost = (url: string): string => {
  try { return new URL(url).hostname.replace('www.', '').slice(0, 22); } catch { return url.slice(0, 22); }
};

const countryFlag = (code: string): string => {
  if (!code || code.length !== 2) return '🌐';
  const c = code.toUpperCase();
  return String.fromCodePoint(0x1F1E6 + c.charCodeAt(0) - 65) +
    String.fromCodePoint(0x1F1E6 + c.charCodeAt(1) - 65);
};

// ── Mini components ───────────────────────────────────────────────────────────

const PulseDot: React.FC<{ up: boolean; size?: number }> = ({ up, size = 9 }) => (
  <span style={{ position: 'relative', display: 'inline-flex', width: size, height: size, flexShrink: 0 }}>
    {up && (
      <span style={{
        position: 'absolute', inset: 0, borderRadius: '50%', background: '#10b981', opacity: 0.4,
        animation: 'ca-ping 1.5s cubic-bezier(0,0,0.2,1) infinite',
      }} />
    )}
    <span style={{ width: size, height: size, borderRadius: '50%', background: up ? '#10b981' : '#ef4444', display: 'inline-block' }} />
  </span>
);

// ECG-style line — draws from left, ghost of prev sweep behind, red blink when down
const EcgLine: React.FC<{ points: HealthPoint[] }> = ({ points }) => {
  const W = 300, H = 44;
  const last = points.slice(-60);
  if (!last.length) return <div style={{ fontSize: 11, color: '#cbd5e1' }}>Waiting for data…</div>;

  const latestStatus = last[last.length - 1]?.status;
  const isDown = latestStatus === 'down';

  const maxRt = Math.max(...last.map(p => p.status === 'up' ? (p.response_time || 0) : 0), 500);
  const coords = last.map((p, i) => ({
    x: (i / Math.max(last.length - 1, 1)) * W,
    y: p.status === 'down'
      ? H - 4
      : H - 6 - Math.min((p.response_time || 0) / maxRt, 1) * (H - 16),
    status: p.status,
    rt: p.response_time || 0,
  }));

  type Seg = { pts: typeof coords; color: string };
  const segments: Seg[] = [];
  if (coords.length) {
    const segColor = (c: typeof coords[0]) =>
      c.status === 'down' ? '#ef4444' : c.rt > 1500 ? '#f59e0b' : '#10b981';
    let seg: Seg = { pts: [coords[0]], color: segColor(coords[0]) };
    for (let i = 1; i < coords.length; i++) {
      const col = segColor(coords[i]);
      if (col !== seg.color) { seg.pts.push(coords[i]); segments.push(seg); seg = { pts: [coords[i]], color: col }; }
      else { seg.pts.push(coords[i]); }
    }
    segments.push(seg);
  }

  const ptStr = (seg: Seg) => seg.pts.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} style={{ display: 'block' }}>
      <defs>
        {/* Growing reveal clip — simulates pen drawing left→right */}
        <clipPath id="ecg-reveal">
          <rect x={0} y={0} height={H}>
            <animate attributeName="width" from="0" to={`${W}`} dur="5s" repeatCount="indefinite" />
          </rect>
        </clipPath>
      </defs>

      {/* Baseline */}
      <line x1={0} y1={H - 1} x2={W} y2={H - 1} stroke="#e2e8f0" strokeWidth={1} />

      {/* Ghost (previous sweep, faded) */}
      {segments.map((seg, i) => (
        <polyline key={`g${i}`} points={ptStr(seg)} fill="none"
          stroke={seg.color} strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round" opacity={0.15} />
      ))}

      {/* Live drawing sweep (clipPath reveals left→right) */}
      <g clipPath="url(#ecg-reveal)">
        {segments.map((seg, i) => (
          <polyline key={i} points={ptStr(seg)} fill="none"
            stroke={seg.color} strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" />
        ))}
        {/* Glowing tip at latest data point */}
        {coords[coords.length - 1] && (
          <circle cx={coords[coords.length - 1].x} cy={coords[coords.length - 1].y}
            r={3.5} fill={isDown ? '#ef4444' : '#10b981'} opacity={0.9} />
        )}
      </g>

      {/* Red pulse overlay when down */}
      {isDown && (
        <rect x={0} y={0} width={W} height={H} fill="#ef4444" rx={3}>
          <animate attributeName="opacity" values="0.06;0.18;0.06" dur="0.9s" repeatCount="indefinite" />
        </rect>
      )}
    </svg>
  );
};

const MiniBar: React.FC<{ value: number; max?: number; color: string }> = ({ value, max = 100, color }) => (
  <div style={{ height: 4, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden', flex: 1 }}>
    <div style={{ height: '100%', width: `${Math.min(100, (value / max) * 100)}%`, background: color, borderRadius: 3, transition: 'width 0.6s ease' }} />
  </div>
);

const DONUT_COLORS = ['#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#f43f5e', '#8b5cf6', '#64748b'];

const CountryDonut: React.FC<{ data: { country: string; country_code?: string; count: number }[] }> = ({ data }) => {
  if (!data.length) return <div style={{ fontSize: '0.75rem', color: '#cbd5e1', padding: '12px 0' }}>No country data yet</div>;
  const total = data.reduce((s, d) => s + d.count, 0);
  const size = 88;
  const r = 32;
  const cx = size / 2;
  const cy = size / 2;
  let startAngle = -Math.PI / 2;
  const slices = data.map((d, i) => {
    const angle = (d.count / total) * 2 * Math.PI;
    const endAngle = startAngle + angle;
    const x1 = cx + r * Math.cos(startAngle);
    const y1 = cy + r * Math.sin(startAngle);
    const x2 = cx + r * Math.cos(endAngle);
    const y2 = cy + r * Math.sin(endAngle);
    const largeArc = angle > Math.PI ? 1 : 0;
    const path = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
    const slice = { path, color: DONUT_COLORS[i % DONUT_COLORS.length], ...d };
    startAngle = endAngle;
    return slice;
  });
  const inner = 18; // donut hole radius
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      {/* SVG donut */}
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <svg width={size} height={size}>
          {slices.map((s, i) => (
            <path key={i} d={s.path} fill={s.color} opacity={0.88} />
          ))}
          {/* Hole */}
          <circle cx={cx} cy={cy} r={inner} fill="#fff" />
          {/* Total label */}
          <text x={cx} y={cy + 1} textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 11, fontWeight: 800, fill: '#334155' }}>
            {total.toLocaleString()}
          </text>
        </svg>
      </div>
      {/* Legend */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0 }}>
        {slices.map((s, i) => {
          const pct = Math.round((s.count / total) * 100);
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
              <span style={{ fontSize: '0.7rem', color: '#475569', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {s.country_code ? countryFlag(s.country_code) : '🌐'} {s.country}
              </span>
              <span style={{ fontSize: '0.68rem', fontWeight: 700, color: s.color, flexShrink: 0 }}>{pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TrendArrow: React.FC<{ prev?: number; curr?: number }> = ({ prev, curr }) => {
  if (prev == null || curr == null) return <span style={{ color: '#cbd5e1', fontSize: 11 }}>–</span>;
  if (curr < prev) return <span style={{ color: '#10b981', fontSize: 12 }}>↑</span>;
  if (curr > prev) return <span style={{ color: '#ef4444', fontSize: 12 }}>↓</span>;
  return <span style={{ color: '#94a3b8', fontSize: 12 }}>→</span>;
};

// ── Main ──────────────────────────────────────────────────────────────────────

const MainDashboardPage: React.FC = () => {
  const { currentAsset, user } = useAuth();
  const navigate = useNavigate();
  const assetId = currentAsset?.id;

  const [planFeatures, setPlanFeatures] = useState<Record<string, boolean>>({});
  const [planLoading, setPlanLoading] = useState(true);

  // Live / Analytics
  const [liveCount, setLiveCount] = useState<number | null>(null);
  const [todayViews, setTodayViews] = useState<number | null>(null);
  const [uniqueVisitors, setUniqueVisitors] = useState<number | null>(null);
  const [visitors, setVisitors] = useState<VisitorRow[]>([]);
  const prevIds = useRef<Set<string>>(new Set());
  const [topCountries, setTopCountries] = useState<{ country: string; country_code?: string; count: number }[]>([]);

  // Monitoring
  const [monChecks, setMonChecks] = useState<HealthCheck[]>([]);
  const [monTimeline, setMonTimeline] = useState<HealthPoint[]>([]);
  const [monToday, setMonToday] = useState(0);
  const [monWeek, setMonWeek] = useState(0);
  const [monMonth, setMonMonth] = useState(0);
  const [monUptime, setMonUptime] = useState<number | null>(null);

  // SEO
  const [seoScore, setSeoScore] = useState<number | null>(null);
  const [seoCritical, setSeoCritical] = useState(0);
  const [seoPages, setSeoPages] = useState(0);
  const [seoLastScan, setSeoLastScan] = useState<string | null>(null);
  const [kwRanks, setKwRanks] = useState<KwRank[]>([]);
  const [kwSearch, setKwSearch] = useState('');

  // Security
  const [secScore, setSecScore] = useState<number | null>(null);
  const [secGrade, setSecGrade] = useState<string | null>(null);
  const [secIssues, setSecIssues] = useState(0);
  const [secSuggestions, setSecSuggestions] = useState<{ title: string; severity: string }[]>([]);
  const [secThreats, setSecThreats] = useState(0);

  // Alerts
  const [recentAlerts, setRecentAlerts] = useState<{ id: number; message?: string; title?: string; severity?: string; source_type?: string; created_at?: string; status?: string }[]>([]);

  // Servers
  const [servers, setServers] = useState<ServerRow[]>([]);

  const [loading, setLoading] = useState(true);

  // ── Plan ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    subscriptionAPI.getPlanLimits()
      .then(r => { const d = r.data?.data; if (d) setPlanFeatures(d.features || {}); })
      .catch(() => {}).finally(() => setPlanLoading(false));
  }, []);

  // ── Fetch all ───────────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    if (!assetId) { setLoading(false); return; }

    await Promise.allSettled([

      // Live count
      analyticsAPI.getLiveVisitors(assetId).then(r => {
        const d = r.data?.data;
        const raw = d?.live_visitors ?? d?.liveVisitors ?? d?.count ?? d?.active_sessions;
        const n = typeof raw === 'number' ? raw : Array.isArray(d?.visitors) ? d.visitors.length : 0;
        setLiveCount(n);
      }),

      // Today stats
      analyticsAPI.getDashboardStats(assetId, 'today').then(r => {
        const d = r.data?.data;
        setTodayViews(d?.total_page_views ?? d?.pageViews ?? d?.page_views ?? 0);
        setUniqueVisitors(d?.unique_visitors ?? d?.uniqueVisitors ?? d?.visitors ?? d?.unique_sessions ?? d?.sessions ?? 0);
      }),

      // Countries (7d for reliable coverage)
      analyticsAPI.getDashboardStats(assetId, '7d').then(r => {
        const d = r.data?.data;
        const countries = d?.top_countries ?? d?.topCountries ?? [];
        if (Array.isArray(countries)) setTopCountries(countries.slice(0, 4));
      }),

      // Visitor feed
      visitorLogAPI.getToday(assetId, 1, 12).then(r => {
        const raw: VisitorRow[] = r.data?.data?.visitors || r.data?.data || [];
        const arr = Array.isArray(raw) ? raw : [];
        const marked = arr.map(v => ({ ...v, _isNew: !prevIds.current.has(v.visitor_id) }));
        prevIds.current = new Set(arr.map(v => v.visitor_id));
        setVisitors(marked);
      }),

      // Monitoring — health log (6h) for timeline + current checks
      monitoringAPI.getHealthLog(assetId, 6).then(r => {
        const d = r.data?.data;
        if (!d) return;
        // Current check status per URL
        const recent: HealthCheck[] = (d.recent_checks || []).reduce((acc: HealthCheck[], c: any) => {
          if (!acc.find(x => x.url === c.url)) {
            acc.push({ url: c.url, status: c.status || (c.is_up ? 'up' : 'down'), response_time: c.response_time, last_checked: c.checked_at });
          }
          return acc;
        }, []);
        setMonChecks(recent);
        setMonTimeline((d.uptime_timeline || []).map((p: any) => ({
          time: p.time, status: p.status, response_time: p.response_time || 0, url: p.url,
        })));
        setMonUptime(d.uptime_percentage ?? null);
      }),

      // Downtime summary for counts
      monitoringAPI.getDowntimeSummary(assetId, 30).then(r => {
        const summary = r.data?.data?.summary || [];
        const today = new Date().toISOString().slice(0, 10);
        const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
        setMonToday(summary.filter((s: any) => s.date === today).reduce((n: number, s: any) => n + s.incident_count, 0));
        setMonWeek(summary.filter((s: any) => s.date >= weekAgo).reduce((n: number, s: any) => n + s.incident_count, 0));
        setMonMonth(summary.reduce((n: number, s: any) => n + s.incident_count, 0));
      }),

      // SEO
      seoAPI.getResults(assetId).then(r => {
        const results: any[] = r.data?.data?.results || [];
        const done = results.filter(x => x.status === 'completed');
        if (done.length) {
          const avg = Math.round(done.reduce((s, x) => s + (x.seo_score || 0), 0) / done.length);
          setSeoScore(avg);
          setSeoPages(done.length);
          setSeoLastScan(done[0]?.created_at || null);
          const allIssues = done.flatMap((x: any) => {
            try { return typeof x.issues === 'string' ? JSON.parse(x.issues) : (x.issues || []); } catch { return []; }
          });
          setSeoCritical(allIssues.filter((i: any) => i.severity === 'critical').length);
        }
      }),

      // Keyword ranks
      seoAPI.getRanks(assetId).then(r => {
        const ranks: KwRank[] = r.data?.data?.ranks || [];
        setKwRanks(ranks.sort((a, b) => (a.google?.rank ?? 9999) - (b.google?.rank ?? 9999)));
      }),

      // Security score
      aiSecurityAPI.getAuditScore(assetId).then(r => {
        const d = r.data?.data;
        const auditScore = d?.auditScore;
        if (auditScore) {
          setSecScore(auditScore.score?.overallScore ?? null);
          setSecGrade(auditScore.score?.riskLevel ?? null);
          const issueCount = (auditScore.metrics?.malwareDetected || 0)
            + (auditScore.metrics?.criticalSuggestions || 0)
            + (auditScore.metrics?.highSuggestions || 0);
          setSecIssues(issueCount);
        }
      }),

      // Security suggestions (top recommendations)
      aiSecurityAPI.getSuggestions(assetId, {}).then(r => {
        const sug: any[] = r.data?.data?.suggestions || [];
        setSecSuggestions(sug.filter(s => !s.dismissed).slice(0, 3).map(s => ({ title: s.title || s.message || s.description || 'Security issue', severity: s.severity || s.priority || 'medium' })));
      }).catch(() => {}),

      // Security threats
      aiSecurityAPI.getMalwareDetections(assetId, {}).then(r => {
        const det: any[] = r.data?.data?.detections || [];
        setSecThreats(det.filter(d => !d.resolved && !d.is_false_positive).length);
      }).catch(() => {}),

      // Servers (scoped to asset)
      serverAPI.list({ asset_id: assetId }).then(r => {
        const raw = r.data?.data?.servers || r.data?.data || [];
        setServers(Array.isArray(raw) ? raw.slice(0, 8) : []);
      }),

      // Recent alerts
      alertsAPI.getByAsset(assetId, { days: 7 }).then(r => {
        const raw = r.data?.data?.alerts || r.data?.data || [];
        setRecentAlerts(Array.isArray(raw) ? raw.slice(0, 6) : []);
      }).catch(() => {}),
    ]);

    setLoading(false);
  }, [assetId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Live poll every 30s
  useEffect(() => {
    if (!assetId) return;
    const tick = () => {
      analyticsAPI.getLiveVisitors(assetId).then(r => { const d = r.data?.data; const raw = d?.live_visitors ?? d?.liveVisitors ?? d?.count ?? d?.active_sessions; setLiveCount(typeof raw === 'number' ? raw : Array.isArray(d?.visitors) ? d.visitors.length : 0); }).catch(() => {});
      visitorLogAPI.getToday(assetId, 1, 12).then(r => {
        const raw: VisitorRow[] = r.data?.data?.visitors || r.data?.data || [];
        const arr = Array.isArray(raw) ? raw : [];
        const marked = arr.map(v => ({ ...v, _isNew: !prevIds.current.has(v.visitor_id) }));
        prevIds.current = new Set(arr.map(v => v.visitor_id));
        setVisitors(marked);
      }).catch(() => {});
      monitoringAPI.getHealthLog(assetId, 1).then(r => {
        const d = r.data?.data;
        if (!d) return;
        const recent: HealthCheck[] = (d.recent_checks || []).reduce((acc: HealthCheck[], c: any) => {
          if (!acc.find((x: HealthCheck) => x.url === c.url)) {
            acc.push({ url: c.url, status: c.status || (c.is_up ? 'up' : 'down'), response_time: c.response_time, last_checked: c.checked_at });
          }
          return acc;
        }, []);
        setMonChecks(recent);
        setMonTimeline(prev => {
          const newPts: HealthPoint[] = (d.uptime_timeline || []).map((p: any) => ({
            time: p.time, status: p.status, response_time: p.response_time || 0, url: p.url,
          }));
          const combined = [...prev, ...newPts.filter(p => !prev.find(x => x.time === p.time))];
          return combined.slice(-60);
        });
      }).catch(() => {});
    };
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [assetId]);

  // ── Derived ─────────────────────────────────────────────────────────────────
  const isFeatureLocked = (f?: string) => !f || planLoading ? false : isPlanFeatureLocked(planFeatures, f);
  const allUp = monChecks.length > 0 && monChecks.every(c => c.status === 'up');
  const monDown = monChecks.filter(c => c.status !== 'up').length;
  const onlineSrv = servers.filter(s => s.status === 'online').length;
  const seoColor = (s: number) => s >= 80 ? '#10b981' : s >= 60 ? '#f59e0b' : '#ef4444';
  const seoLabel = (s: number) => s >= 80 ? 'Excellent' : s >= 60 ? 'Good' : s >= 40 ? 'Needs work' : 'Poor';
  const secColor = secScore !== null ? (secScore >= 80 ? '#10b981' : secScore >= 60 ? '#f59e0b' : '#ef4444') : '#94a3b8';

  const filteredKw = kwSearch.trim()
    ? kwRanks.filter(k => k.keyword.toLowerCase().includes(kwSearch.toLowerCase()))
    : kwRanks.slice(0, 5);

  // Card shell style
  const cs = (extra?: React.CSSProperties): React.CSSProperties => ({
    background: '#fff', borderRadius: 16, border: '1px solid #e9eef4',
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)', overflow: 'hidden', ...extra,
  });

  const cardHeader = (title: string, icon: string, color: string, link?: { to: string; label: string }) => (
    <div style={{ padding: '13px 18px 11px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 7 }}>
      <i className={`bi ${icon}`} style={{ fontSize: 14, color }}></i>
      <span style={{ fontWeight: 700, fontSize: '0.8rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</span>
      {link && (
        <Link to={link.to} style={{ marginLeft: 'auto', fontSize: '0.72rem', color: '#6366f1', textDecoration: 'none', fontWeight: 600 }}>
          {link.label} →
        </Link>
      )}
    </div>
  );

  return (
    <DashboardLayout noAssetRequired>
      <style>{`
        @keyframes ca-ping { 75%, 100% { transform: scale(2.2); opacity: 0; } }
        @keyframes ca-blink { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }
        @keyframes ca-slide { from { opacity:0; transform:translateY(-5px); } to { opacity:1; transform:translateY(0); } }
        .ca-new { animation: ca-slide 0.35s ease both; }
        .dash-srv-card:hover { box-shadow: 0 6px 18px rgba(0,0,0,0.10) !important; transform: translateY(-1px); }
        .dash-app-btn:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,0,0,0.10) !important; }
      `}</style>

      <div style={{ background: '#f0f4f8', minHeight: '100vh', padding: '0 20px 20px' }}>

        {/* Welcome */}
        <div style={{ marginBottom: 12, paddingTop: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
          <div>
            <h1 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>
              Welcome{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
            </h1>
            {currentAsset && <p style={{ color: '#64748b', margin: '1px 0 0', fontSize: '0.78rem' }}><i className="bi bi-box-seam me-1"></i>{currentAsset.name}</p>}
          </div>
          <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>
            {new Date().toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
          </span>
        </div>

        <OnboardingBanner />

        {assetId ? <AssetHealthPanel assetId={assetId} /> : null}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>

          {/* ── LEFT: Apps card (half-width, spans rows 1-2) ─────────── */}
          <div style={{ ...cs(), gridColumn: 'span 2', gridRow: 'span 2' }}>
            {cardHeader('Apps', 'bi-grid-fill', '#6366f1')}
            <div style={{ padding: '16px 14px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {APP_ITEMS.map((app, i) => {
                const locked = isFeatureLocked(app.feature);
                return (
                  <button key={i} onClick={() => navigate(locked ? '/billing/plans' : app.route)}
                    className="dash-app-btn"
                    style={{
                      background: locked ? '#f8fafc' : '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
                      padding: '14px 8px', cursor: 'pointer', opacity: locked ? 0.55 : 1,
                      transition: 'all 0.15s', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                      textAlign: 'center',
                    }}
                    title={locked ? `Upgrade to unlock ${app.title}` : app.description}
                  >
                    <div style={{
                      width: 52, height: 52, borderRadius: 14, background: app.gradient,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      position: 'relative', boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
                    }}>
                      <i className={`bi ${app.icon}`} style={{ fontSize: 22, color: '#fff' }}></i>
                      {locked && (
                        <div style={{ position: 'absolute', top: -4, right: -4, width: 16, height: 16, borderRadius: '50%', background: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <i className="bi bi-lock-fill" style={{ color: '#fff', fontSize: 8 }}></i>
                        </div>
                      )}
                    </div>
                    <span style={{ fontWeight: 600, color: '#334155', fontSize: '0.72rem', lineHeight: 1.2 }}>{app.title}</span>
                    <span style={{ color: '#94a3b8', fontSize: '0.62rem', lineHeight: 1.3, display: 'block' }}>{app.description}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── RIGHT: Data cards (3 rows × 2 cols) ──────────────────── */}
          {!assetId ? (
            <div style={{ ...cs({ padding: 28, textAlign: 'center' }), gridColumn: 'span 2' }}>
              <i className="bi bi-box-seam" style={{ fontSize: 30, color: '#cbd5e1' }}></i>
              <p style={{ color: '#64748b', margin: '10px 0 0', fontSize: '0.85rem' }}>
                No asset selected. <Link to="/assets">Add or select an asset</Link> to see live data.
              </p>
            </div>
          ) : (
          <>

            {/* ── 1. LIVE NOW ─────────────────────────────────────────────── */}
            <div style={cs()}>
              {cardHeader('Live Now', 'bi-broadcast', '#6366f1', { to: '/analytics/visitor-log', label: 'Visitor log' })}
              <div style={{ padding: '12px 16px' }}>
                {/* Live count */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <PulseDot up={(liveCount ?? 0) > 0} size={10} />
                  <span style={{ fontSize: '2.2rem', fontWeight: 800, color: '#6366f1', lineHeight: 1 }}>
                    {loading ? <span className="spinner-border spinner-border-sm text-primary" /> : (liveCount ?? 0)}
                  </span>
                  <span style={{ fontSize: '0.72rem', color: '#94a3b8', paddingBottom: 4 }}>active now</span>
                </div>
                {/* Today stats */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                  {[
                    { label: 'Views', val: todayViews, icon: 'bi-eye-fill', color: '#0ea5e9' },
                    { label: 'Unique', val: uniqueVisitors, icon: 'bi-people-fill', color: '#8b5cf6' },
                  ].map((s, i) => (
                    <div key={i} style={{ flex: 1, background: s.color + '0d', borderRadius: 8, padding: '6px 8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                        <i className={`bi ${s.icon}`} style={{ fontSize: 10, color: s.color }}></i>
                        <span style={{ fontSize: '0.62rem', color: s.color, fontWeight: 600 }}>{s.label}</span>
                      </div>
                      <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>
                        {loading ? '—' : (s.val ?? 0).toLocaleString()}
                      </div>
                      <div style={{ fontSize: '0.58rem', color: '#94a3b8' }}>today</div>
                    </div>
                  ))}
                </div>
                {/* Live visitors feed */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
                  <PulseDot up={visitors.length > 0} size={6} />
                  <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Live</span>
                </div>
                <div style={{ overflowY: 'auto', maxHeight: 88 }}>
                  {visitors.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 11, padding: '10px 0' }}>
                      <i className="bi bi-people" style={{ display: 'block', marginBottom: 3 }}></i>
                      No visitors yet
                    </div>
                  ) : visitors.slice(0, 3).map((v, i) => (
                    <div key={v.visitor_id + i} className={v._isNew ? 'ca-new' : ''}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 7, padding: '5px 0',
                        borderBottom: '1px solid #f8fafc',
                        background: v._isNew ? '#f0fdf4' : undefined,
                        transition: 'background 2s',
                      }}>
                      <i className={`bi ${v.device_type === 'mobile' ? 'bi-phone' : v.device_type === 'tablet' ? 'bi-tablet' : 'bi-display'}`} style={{ fontSize: 10, color: '#7c3aed', flexShrink: 0 }}></i>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {v.city && v.country ? `${v.city}, ${v.country}` : v.country || v.city || 'Unknown'}
                        </div>
                      </div>
                      <span style={{ fontSize: '0.62rem', color: '#cbd5e1', flexShrink: 0 }}>{timeAgo(v.started_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── 2. ANALYTICS ────────────────────────────────────────────── */}
            <div style={cs()}>
              {cardHeader('Analytics', 'bi-bar-chart-line-fill', '#6366f1', { to: '/analytics/summary', label: 'Details' })}
              <div style={{ padding: '12px 16px' }}>
                {/* Today stats */}
                <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                  {[
                    { label: 'Page Views', val: todayViews, icon: 'bi-eye-fill', color: '#0ea5e9' },
                    { label: 'Unique Visitors', val: uniqueVisitors, icon: 'bi-people-fill', color: '#6366f1' },
                  ].map((s, i) => (
                    <div key={i} style={{
                      flex: 1, background: s.color + '0d', borderRadius: 10, padding: '8px 10px',
                      display: 'flex', flexDirection: 'column', gap: 2,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <i className={`bi ${s.icon}`} style={{ fontSize: 11, color: s.color }}></i>
                        <span style={{ fontSize: '0.65rem', color: s.color, fontWeight: 600 }}>{s.label}</span>
                      </div>
                      <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>
                        {loading ? '—' : (s.val ?? 0).toLocaleString()}
                      </div>
                      <div style={{ fontSize: '0.62rem', color: '#94a3b8' }}>today</div>
                    </div>
                  ))}
                </div>

                {/* Countries donut */}
                <div style={{ fontSize: '0.67rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Visitors by Country</div>
                <CountryDonut data={topCountries} />
              </div>
            </div>

            {/* ── 3. MONITORING ────────────────────────────────────────────── */}
            <div style={cs()}>
              {cardHeader('Monitoring', 'bi-activity', allUp ? '#10b981' : monDown > 0 ? '#ef4444' : '#94a3b8', { to: '/monitoring/live', label: 'Live view' })}
              <div style={{ padding: '12px 16px' }}>
                {monChecks.length === 0 && monTimeline.length === 0 ? (
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8', paddingTop: 4 }}>
                    No monitoring data yet.{' '}
                    <Link to="/monitoring/live" style={{ color: '#6366f1' }}>Set up monitoring →</Link>
                  </div>
                ) : (
                  <>
                    {/* Status badge */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                      <span style={{
                        fontSize: '1.4rem', fontWeight: 800, lineHeight: 1,
                        color: allUp ? '#10b981' : monDown > 0 ? '#ef4444' : '#94a3b8',
                      }}>
                        {monChecks.length === 0 ? '—' : allUp ? 'All Up' : `${monDown} Down`}
                      </span>
                      {monUptime !== null && (
                        <span style={{ fontSize: '0.72rem', color: '#94a3b8', paddingBottom: 2 }}>{monUptime}% uptime</span>
                      )}
                    </div>

                    {/* ECG timeline */}
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: '0.67rem', color: '#94a3b8', marginBottom: 4 }}>Response time / uptime</div>
                      <EcgLine points={monTimeline} />
                    </div>

                    {/* Endpoint dots + status */}
                    {monChecks.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
                        {monChecks.slice(0, 4).map((c, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <PulseDot up={c.status === 'up'} size={7} />
                            <span style={{ fontSize: '0.72rem', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                              {shortHost(c.url)}
                            </span>
                            {c.response_time && <span style={{ fontSize: '0.67rem', color: '#94a3b8', flexShrink: 0 }}>{c.response_time}ms</span>}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Downtime counts */}
                    <div style={{ display: 'flex', gap: 10, borderTop: '1px solid #f1f5f9', paddingTop: 8 }}>
                      {[
                        { label: 'Today', val: monToday },
                        { label: '7 days', val: monWeek },
                        { label: '30 days', val: monMonth },
                      ].map((s, i) => (
                        <Link key={i} to="/monitoring/live" style={{ flex: 1, textAlign: 'center', textDecoration: 'none' }}>
                          <div style={{ fontSize: '1rem', fontWeight: 800, color: s.val > 0 ? '#ef4444' : '#10b981' }}>{s.val}</div>
                          <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>incidents {s.label}</div>
                        </Link>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* ── 4. SEO ─────────────────────────────────────────────────────── */}
            <div style={cs()}>
              {cardHeader('SEO', 'bi-graph-up-arrow', '#10b981', { to: '/seo/summary', label: 'Full report' })}
              <div style={{ padding: '12px 16px' }}>
                {seoScore !== null ? (
                  <>
                    {/* Score bar */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                      <div style={{ textAlign: 'center', minWidth: 56 }}>
                        <div style={{ fontSize: '2rem', fontWeight: 900, color: seoColor(seoScore), lineHeight: 1 }}>{seoScore}</div>
                        <div style={{ fontSize: '0.65rem', fontWeight: 700, color: seoColor(seoScore), textTransform: 'uppercase' }}>{seoLabel(seoScore)}</div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ height: 8, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden', marginBottom: 4 }}>
                          <div style={{ height: '100%', width: `${seoScore}%`, background: `linear-gradient(90deg, ${seoColor(seoScore)}, ${seoColor(seoScore)}aa)`, borderRadius: 4, transition: 'width 0.8s ease' }} />
                        </div>
                        <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                          {seoPages} pages · {seoCritical > 0 ? <span style={{ color: '#ef4444' }}>{seoCritical} critical</span> : <span style={{ color: '#10b981' }}>no critical issues</span>}
                          {seoLastScan && <span> · {timeAgo(seoLastScan)} ago</span>}
                        </div>
                      </div>
                    </div>

                    {/* Keyword search */}
                    {kwRanks.length > 0 && (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                          <div style={{ fontSize: '0.67rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', flex: 1 }}>Keyword Rankings</div>
                          <input
                            value={kwSearch}
                            onChange={e => setKwSearch(e.target.value)}
                            placeholder="filter…"
                            style={{
                              fontSize: '0.7rem', border: '1px solid #e2e8f0', borderRadius: 6, padding: '2px 7px',
                              outline: 'none', width: 70, color: '#475569', background: '#f8fafc',
                            }}
                          />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                          {filteredKw.slice(0, 3).map((k, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                              <span style={{ fontSize: '0.7rem', color: '#334155', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{k.keyword}</span>
                              {k.google?.rank ? (
                                <span style={{
                                  fontSize: '0.68rem', fontWeight: 700, padding: '1px 6px', borderRadius: 10,
                                  background: k.google.rank <= 10 ? '#d1fae5' : k.google.rank <= 30 ? '#fef3c7' : '#f1f5f9',
                                  color: k.google.rank <= 10 ? '#059669' : k.google.rank <= 30 ? '#d97706' : '#64748b',
                                }}>#{k.google.rank}</span>
                              ) : (
                                <span style={{ fontSize: '0.68rem', color: '#cbd5e1' }}>—</span>
                              )}
                              <TrendArrow curr={k.google?.rank} />
                            </div>
                          ))}
                          {filteredKw.length === 0 && <div style={{ fontSize: '0.72rem', color: '#cbd5e1' }}>No keywords match</div>}
                        </div>
                      </>
                    )}
                    {kwRanks.length === 0 && (
                      <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 4 }}>
                        <Link to="/seo/scanner" style={{ color: '#6366f1' }}>Add keywords to track rankings →</Link>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: '0.78rem', color: '#94a3b8', paddingTop: 4 }}>
                    No scans yet.<br />
                    <Link to="/seo/scanner" style={{ color: '#6366f1' }}>Start an SEO scan →</Link>
                  </div>
                )}
              </div>
            </div>

            {/* ── 5. SECURITY ──────────────────────────────────────────────── */}
            <div style={cs()}>
              {cardHeader('Security', 'bi-shield-fill-check', '#f43f5e', { to: '/security/ai-advisor', label: 'Full audit' })}
              <div style={{ padding: '12px 16px' }}>
                {secGrade || secScore !== null ? (
                  <>
                    {/* Score row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 9 }}>
                      {/* Score badge — show numeric score or single letter grade */}
                      <div style={{
                        width: 48, height: 48, borderRadius: 11, flexShrink: 0,
                        background: secColor + '15', border: `2px solid ${secColor}`,
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <span style={{ fontSize: secScore !== null ? '1.1rem' : '1.4rem', fontWeight: 900, color: secColor, lineHeight: 1 }}>
                          {secScore !== null ? secScore : secGrade ? secGrade.charAt(0).toUpperCase() : '?'}
                        </span>
                        {secScore !== null && (
                          <span style={{ fontSize: '0.5rem', color: secColor, fontWeight: 700, opacity: 0.8, marginTop: 1 }}>/100</span>
                        )}
                      </div>
                      {/* Risk label + bar + badges */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0f172a', marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {(() => {
                            if (secGrade === 'low') return 'Well protected';
                            if (secGrade === 'medium') return 'Moderate risk';
                            if (secGrade === 'high' || secGrade === 'critical') return 'High risk';
                            if (secScore !== null) return secScore >= 80 ? 'Well protected' : secScore >= 60 ? 'Moderate risk' : 'High risk';
                            return 'Security audit';
                          })()}
                        </div>
                        <div style={{ height: 5, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden', marginBottom: 5 }}>
                          <div style={{ height: '100%', width: `${secScore ?? 0}%`, background: secColor, borderRadius: 3, transition: 'width 0.4s ease' }} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          {secIssues > 0 && (
                            <span style={{ fontSize: '0.63rem', color: '#ef4444', fontWeight: 600, background: '#fee2e2', borderRadius: 4, padding: '1px 6px' }}>
                              {secIssues} issue{secIssues !== 1 ? 's' : ''}
                            </span>
                          )}
                          {secThreats > 0 && (
                            <span style={{ fontSize: '0.63rem', color: '#dc2626', fontWeight: 700, background: '#fecaca', borderRadius: 4, padding: '1px 6px', animation: 'ca-blink 1.5s ease-in-out infinite' }}>
                              {secThreats} threat{secThreats !== 1 ? 's' : ''}
                            </span>
                          )}
                          {secIssues === 0 && secThreats === 0 && (
                            <span style={{ fontSize: '0.63rem', color: '#10b981', fontWeight: 600, background: '#dcfce7', borderRadius: 4, padding: '1px 6px' }}>
                              ✓ No threats
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* AI recommendations */}
                    {secSuggestions.length > 0 && (
                      <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>AI Recommendations</div>
                        {secSuggestions.map((s, i) => {
                          const sColor = s.severity === 'critical' || s.severity === 'high' ? '#ef4444' : s.severity === 'medium' ? '#f59e0b' : '#10b981';
                          return (
                            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                              <i className="bi bi-exclamation-circle-fill" style={{ fontSize: 10, color: sColor, flexShrink: 0, marginTop: 2 }}></i>
                              <span style={{ fontSize: '0.7rem', color: '#475569', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{s.title}</span>
                            </div>
                          );
                        })}
                        <Link to="/security/ai-advisor" style={{ fontSize: '0.68rem', color: '#6366f1', textDecoration: 'none', marginTop: 2 }}>View all →</Link>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: '0.78rem', color: '#94a3b8', paddingTop: 4 }}>
                    No audit yet.<br />
                    <Link to="/security/ai-advisor" style={{ color: '#6366f1' }}>Run a security audit →</Link>
                  </div>
                )}
              </div>
            </div>

            {/* ── 6. SERVERS ─────────────────────────────────────────────── */}
            <div style={cs()}>
              {cardHeader(
                `Servers ${onlineSrv}/${servers.length}`,
                'bi-hdd-stack-fill', '#f59e0b',
                { to: '/servers', label: 'All' }
              )}
              <div style={{ padding: '4px 0' }}>
                {servers.length === 0 ? (
                  <div style={{ fontSize: '0.78rem', color: '#94a3b8', padding: '8px 16px' }}>
                    No servers for this asset.{' '}
                    <Link to="/servers/register" style={{ color: '#6366f1' }}>Register →</Link>
                  </div>
                ) : (
                  <>
                    {/* Warnings */}
                    {servers.some(s => s.status !== 'online' || (s.current_metrics?.cpu_usage_percent ?? 0) > 80 || (s.current_metrics?.memory_usage_percent ?? 0) > 80 || (s.current_metrics?.disk_worst_percent ?? 0) > 85) && (
                      <div style={{ padding: '5px 14px', background: '#fef3c7', borderBottom: '1px solid #fde68a', display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {servers.filter(s => s.status !== 'online').map(s => (
                          <div key={`down-${s.id}`} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <i className="bi bi-exclamation-circle-fill" style={{ color: '#ef4444', fontSize: 11 }}></i>
                            <span style={{ fontSize: '0.7rem', color: '#92400e', fontWeight: 600 }}>{s.name} is offline</span>
                          </div>
                        ))}
                        {servers.filter(s => s.status === 'online' && ((s.current_metrics?.cpu_usage_percent ?? 0) > 80 || (s.current_metrics?.memory_usage_percent ?? 0) > 80 || (s.current_metrics?.disk_worst_percent ?? 0) > 85)).map(s => {
                          const cpu = s.current_metrics?.cpu_usage_percent ?? 0;
                          const mem = s.current_metrics?.memory_usage_percent ?? 0;
                          const disk = s.current_metrics?.disk_worst_percent ?? 0;
                          const parts = [
                            cpu > 80 ? `CPU ${Math.round(cpu)}%` : '',
                            mem > 80 ? `MEM ${Math.round(mem)}%` : '',
                            disk > 85 ? `DISK ${Math.round(disk)}%` : '',
                          ].filter(Boolean);
                          return (
                            <div key={`alert-${s.id}`} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                              <i className="bi bi-exclamation-triangle-fill" style={{ color: '#d97706', fontSize: 11 }}></i>
                              <span style={{ fontSize: '0.7rem', color: '#92400e' }}>{s.name}: {parts.join(', ')} high</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {/* Server rows */}
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {servers.map(srv => {
                        const online = srv.status === 'online';
                        const cpu = srv.current_metrics?.cpu_usage_percent ?? null;
                        const mem = srv.current_metrics?.memory_usage_percent ?? null;
                        const disk = srv.current_metrics?.disk_worst_percent ?? null;
                        const cpuHot = cpu !== null && cpu > 80;
                        const memHot = mem !== null && mem > 80;
                        const diskHot = disk !== null && disk > 85;
                        return (
                          <Link key={srv.id} to={`/servers/${srv.id}`}
                            style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '6px 14px', borderBottom: '1px solid #f8fafc', textDecoration: 'none' }}
                            onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                          >
                            <i className="bi bi-heart-pulse-fill" style={{
                              fontSize: 11, color: online ? '#10b981' : '#ef4444', flexShrink: 0,
                              animation: online ? 'ca-blink 1.4s ease-in-out infinite' : undefined,
                            }}></i>
                            <span style={{ flex: 1, fontSize: '0.75rem', fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                              {srv.name}
                            </span>
                            <span style={{
                              fontSize: '0.58rem', fontWeight: 700, padding: '1px 5px', borderRadius: 5, flexShrink: 0,
                              background: online ? '#dcfce7' : '#fee2e2',
                              color: online ? '#16a34a' : '#dc2626',
                            }}>{online ? 'UP' : 'DOWN'}</span>
                            {cpu !== null && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                                <span style={{ fontSize: '0.56rem', color: '#94a3b8', width: 20 }}>CPU</span>
                                <div style={{ width: 32, height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                                  <div style={{ height: '100%', width: `${Math.min(100, cpu)}%`, background: cpuHot ? '#ef4444' : '#6366f1', borderRadius: 3 }} />
                                </div>
                                <span style={{ fontSize: '0.56rem', fontWeight: 700, color: cpuHot ? '#ef4444' : '#64748b', width: 22, textAlign: 'right' }}>{Math.round(cpu)}%</span>
                              </div>
                            )}
                            {mem !== null && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                                <span style={{ fontSize: '0.56rem', color: '#94a3b8', width: 20 }}>MEM</span>
                                <div style={{ width: 32, height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                                  <div style={{ height: '100%', width: `${Math.min(100, mem)}%`, background: memHot ? '#ef4444' : '#0ea5e9', borderRadius: 3 }} />
                                </div>
                                <span style={{ fontSize: '0.56rem', fontWeight: 700, color: memHot ? '#ef4444' : '#64748b', width: 22, textAlign: 'right' }}>{Math.round(mem)}%</span>
                              </div>
                            )}
                            {disk !== null && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                                <span style={{ fontSize: '0.56rem', color: '#94a3b8', width: 20 }}>DISK</span>
                                <div style={{ width: 32, height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                                  <div style={{ height: '100%', width: `${Math.min(100, disk)}%`, background: diskHot ? '#ef4444' : '#f59e0b', borderRadius: 3 }} />
                                </div>
                                <span style={{ fontSize: '0.56rem', fontWeight: 700, color: diskHot ? '#ef4444' : '#64748b', width: 22, textAlign: 'right' }}>{Math.round(disk)}%</span>
                              </div>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* ── 7. RECENT ALERTS ────────────────────────────────────── */}
            <div style={{ ...cs(), gridColumn: 'span 2' }}>
              {cardHeader('Recent Alerts', 'bi-bell-fill', '#f59e0b', { to: '/alerts/history', label: 'All alerts' })}
              <div style={{ padding: '8px 0' }}>
                {recentAlerts.length === 0 ? (
                  <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <i className="bi bi-check-circle-fill" style={{ fontSize: 16, color: '#10b981' }}></i>
                    <span style={{ fontSize: '0.78rem', color: '#64748b' }}>No alerts in the last 7 days</span>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
                    {recentAlerts.map((a, i) => {
                      const sev = a.severity || 'info';
                      const sevColor = sev === 'critical' ? '#dc2626' : sev === 'error' ? '#ef4444' : sev === 'warning' ? '#f59e0b' : '#0ea5e9';
                      const sevBg = sev === 'critical' ? '#fef2f2' : sev === 'error' ? '#fff1f2' : sev === 'warning' ? '#fffbeb' : '#f0f9ff';
                      const icon = a.source_type === 'server' ? 'bi-hdd-stack-fill' : a.source_type === 'website_health' ? 'bi-activity' : a.source_type === 'security_scan' ? 'bi-shield-exclamation' : 'bi-exclamation-circle-fill';
                      return (
                        <div key={a.id} style={{
                          display: 'flex', alignItems: 'flex-start', gap: 8, padding: '7px 14px',
                          borderBottom: i < recentAlerts.length - 2 ? '1px solid #f8fafc' : undefined,
                          background: a.status === 'active' ? sevBg : undefined,
                        }}>
                          <i className={`bi ${icon}`} style={{ fontSize: 11, color: sevColor, flexShrink: 0, marginTop: 2 }}></i>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {a.title || a.message || 'Alert'}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
                              <span style={{ fontSize: '0.6rem', fontWeight: 700, padding: '1px 5px', borderRadius: 5, background: sevColor + '18', color: sevColor, textTransform: 'uppercase' }}>{sev}</span>
                              <span style={{ fontSize: '0.6rem', color: '#94a3b8' }}>{timeAgo(a.created_at)}</span>
                            </div>
                          </div>
                          {a.status === 'active' && (
                            <span style={{ fontSize: '0.58rem', fontWeight: 700, padding: '1px 5px', borderRadius: 4, background: '#fef3c7', color: '#d97706', flexShrink: 0 }}>NEW</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

          </>
          )}

        </div>

      </div>
    </DashboardLayout>
  );
};

export default MainDashboardPage;
